import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { getComplianceLibraryColumns, Compliance, ComplianceArchiveRequest, GetCompliancesRequest } from '../../models/compliances.model';
import { CompliancesLibraryService } from '../../services/compliances-library.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ComplianceTasksTableComponent } from '../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ComplianceStatusFilter } from '../../../compliance-overview/models/compliance-item.model';
import { ComplianceStateService } from '../../../_shared/services/compliance-state.service';
import { Router } from '@angular/router';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ComplianceAssignModalComponent } from '../modals/compliance-assign-modal/compliance-assign-modal.component';
import { TranslocoService } from '@jsverse/transloco';
import { translateLookupLabel } from '../../../../../_shared/helpers/lookup-label.helper';
import { translateChecklistTypeName, translateComplianceScheduleName } from '../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-compliances',
  standalone: true,
  imports: [
    ComplianceTasksTableComponent,
    CommonModule,
  ],
  templateUrl: './compliances.component.html',
  styleUrl: './compliances.component.scss'
})
export class CompliancesComponent implements OnInit, OnDestroy {

  public complianceData: Compliance[] = [];

  private complianceService = inject(CompliancesLibraryService);
  private stateService = inject(ComplianceStateService);
  private errorHandlerService = inject(ErrorHandlerService);
  private confirmationModalService = inject(ConfirmationModalService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private transloco = inject(TranslocoService);
  private destroy$: Subject<void> = new Subject<void>();
  private cancellation: Subject<void> = new Subject<void>();

  private accountIds: number[] = [];
  private regionId: number | null = null;
  private countryId: number | null = null;
  private searchTerm: string = '';
  private complianceId: number | undefined = undefined;

  isLoading: boolean = true;
  isLoadingMore: boolean = false;
  hasError: boolean = false;
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalCount: number = 0;

  columns = getComplianceLibraryColumns();

  ngOnInit(): void {
    this.stateService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe(request => {
        this.accountIds = request.accountIds ?? [];
        this.regionId = request.complianceRegion?.type === 'region' ? request.complianceRegion.id : null;
        this.countryId = request.complianceRegion?.type === 'country' ? request.complianceRegion.id : null;
        this.searchTerm = request.searchTerm ?? '';
        this.complianceId = request.complianceId;
        this.resetAndLoad();
      });

    this.stateService.refresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetAndLoad();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancellation.next();
    this.cancellation.complete();
  }

  private async loadCompliancesDataAsync(resetResults: boolean = false): Promise<void> {
    this.hasError = false;
    const requestedPage = this.page;

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancellation.next();
      } else {
        this.isLoadingMore = true;
      }

      const request: GetCompliancesRequest = {
        accountIds: this.accountIds,
        page: requestedPage,
        pageSize: this.pageSize,
        regionId: this.regionId,
        countryId: this.countryId,
        searchTerm: this.searchTerm,
        complianceId: this.complianceId
      };

      const response = await this.complianceService.getCompliancesAsync(request, this.cancellation);

      const result = response.data;
      this.totalCount = result?.totalCount ?? 0;
      this.totalPages = result?.totalPages ?? 0;

      const rows = (result?.results ?? []).map(item => this.toRow(item));

      if (requestedPage === 1) {
        this.complianceData = rows;
      } else {
        this.complianceData = [...this.complianceData, ...rows];
      }

      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (requestedPage > 1) {
            this.page--;
          }

          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.page >= this.totalPages) {
      return;
    }

    this.page++;
    void this.loadCompliancesDataAsync();
  }

  onRowClick(item: Compliance): void {
    this.router.navigate(['/asset-management/compliance-library/compliance-details'], {
      queryParams: {
        complianceId: item.id,
        complianceName: item.complianceName,
        complianceType: item.complianceType,
        checklistSubType: item.checklistSubType
      }
    });
  }

  onAssignClick(item: Compliance): void {
    const modalRef = this.modalService.open(ComplianceAssignModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'lg'
    });
    modalRef.componentInstance.compliance = item;
    modalRef.componentInstance.accountIds = this.accountIds;
    modalRef.closed.subscribe(() => {
      this.resetAndLoad();
    });
  }

  async onArchiveClickAsync(item: Compliance): Promise<void> {
    let confirmed = false;

    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.confirmations.archive.title'),
      message: this.transloco.translate('compliance.library.confirmations.archive.message'),
      warningMessage: this.transloco.translate('compliance.library.confirmations.archive.warning'),
      confirmText: this.transloco.translate('compliance.library.confirmations.archive.confirm'),
      declineText: this.transloco.translate('compliance.library.confirmations.archive.decline'),
      onConfirm: async () => {
        const request: ComplianceArchiveRequest = {
          complianceId: item.id,
          accountIds: this.accountIds
        };
        await this.complianceService.archiveComplianceAsync(request);
        confirmed = true;
      }
    });

    if (confirmed) {
      this.resetAndLoad();
      this.stateService.refresh();
    }
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.resetAndLoad();
  }

  onStatusFilter(status: ComplianceStatusFilter) {
  }

  /** Display labels for catalogue names; the raw names stay on the row for routing and payloads. */
  private toRow(item: Compliance): Compliance {
    return {
      ...item,
      complianceTypeLabel: translateLookupLabel(this.transloco, 'compliance.library.complianceTypes', item.complianceType),
      checklistSubTypeLabel: translateComplianceScheduleName(this.transloco, item.checklistSubType),
      checklistTypeLabel: translateChecklistTypeName(this.transloco, item.checklistType)
    };
  }

  private resetAndLoad(): void {
    this.page = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.complianceData = [];
    void this.loadCompliancesDataAsync(true);
  }
}